import React, { useEffect, useMemo, useState, useRef } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../auth";
import { useI18n } from "../i18n";
import { toast } from "sonner";
import { Lock, CheckCircle2, Clock } from "lucide-react";
import {
  celebratePredictionSaved,
  celebrateExactWin,
  celebrateOutcomeWin,
} from "../lib/celebration";

const formatDate = (iso, lang) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString(lang === "es" ? "es-CO" : lang === "pt" ? "pt-BR" : "en-US", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "America/Bogota",
    });
  } catch {
    return iso;
  }
};

const formatColombiaTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString("es-CO", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "America/Bogota",
    });
  } catch {
    return "";
  }
};

function MatchCard({ match, prediction, onSaved }) {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [home, setHome] = useState(prediction?.home_score ?? "");
  const [away, setAway] = useState(prediction?.away_score ?? "");
  const [saving, setSaving] = useState(false);
  const [celebratedKey, setCelebratedKey] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    setHome(prediction?.home_score ?? "");
    setAway(prediction?.away_score ?? "");
  }, [prediction?.home_score, prediction?.away_score]);

  const kickoffDate = useMemo(() => new Date(match.kickoff), [match.kickoff]);
  const locked = kickoffDate.getTime() <= now || match.status === "finished";
  const finished = match.status === "finished";

  // Live tick every second only while the match is upcoming
  useEffect(() => {
    if (finished) return;
    if (kickoffDate.getTime() <= Date.now()) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [finished, kickoffDate]);

  // Remaining time string (Dd Hh Mm Ss)
  const remaining = useMemo(() => {
    const ms = kickoffDate.getTime() - now;
    if (ms <= 0) return null;
    const totalSec = Math.floor(ms / 1000);
    const d = Math.floor(totalSec / 86400);
    const h = Math.floor((totalSec % 86400) / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    const pad = (n) => String(n).padStart(2, "0");
    if (d > 0) return `${d}d ${pad(h)}h ${pad(m)}m`;
    if (h > 0) return `${pad(h)}h ${pad(m)}m ${pad(s)}s`;
    return `${pad(m)}m ${pad(s)}s`;
  }, [now, kickoffDate]);

  // Urgency: <10 min left → red pulse
  const urgent = remaining && kickoffDate.getTime() - now < 10 * 60 * 1000;

  // Celebrate when a finished match awarded points (only first time we see it)
  useEffect(() => {
    if (!finished || !prediction || prediction.points_awarded === undefined) return;
    const key = `pollagol_celebrated:${prediction.id || prediction.match_id}`;
    if (celebratedKey === key) return;
    if (typeof window !== "undefined" && localStorage.getItem(key)) return;
    if (prediction.points_awarded >= 5) {
      celebrateExactWin();
    } else if (prediction.points_awarded > 0) {
      celebrateOutcomeWin();
    } else {
      return;
    }
    if (typeof window !== "undefined") localStorage.setItem(key, "1");
    setCelebratedKey(key);
  }, [finished, prediction, celebratedKey]);

  const save = async () => {
    if (home === "" || away === "") {
      toast.error(t("enter_scores"));
      return;
    }
    setSaving(true);
    try {
      await api.post("/predictions", {
        match_id: match.id,
        home_score: Number(home),
        away_score: Number(away),
      });
      celebratePredictionSaved();
      toast.success(t("saved_toast"));
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="border border-white/10 bg-[#0d0d0d] hover:border-[#00D37F]/40 transition-colors fade-up overflow-hidden"
      data-testid={`match-card-${match.id}`}
    >
      {/* Header: Stage + kickoff */}
      <div className="flex items-center justify-between px-5 py-3 bg-black/40 border-b border-white/5 font-mono">
        <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#00D37F]">
          {match.stage}
        </span>
        <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/50">
          {finished ? (
            <>
              <CheckCircle2 className="w-3 h-3 text-[#00D37F]" /> {t("ft")}
            </>
          ) : locked ? (
            <>
              <Lock className="w-3 h-3 text-[#FF2D45]" /> {t("locked_chip")}
            </>
          ) : (
            <>
              <Clock className="w-3 h-3" /> {formatDate(match.kickoff, lang)}{" "}
              <span className="text-[#00D37F]/70">({t("colombia_time_short")})</span>
            </>
          )}
        </span>
      </div>

      {/* Scoreboard */}
      <div className="scoreboard relative grid grid-cols-[1fr_auto_1fr] items-center gap-3 px-5 py-6">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-3xl drop-shadow">{match.home_flag}</span>
          <span className="font-head text-base sm:text-lg truncate">{match.home_team}</span>
        </div>
        <div className="flex items-center gap-2">
          {finished ? (
            <>
              <span className="font-mono font-bold text-3xl tabular-nums text-[#00D37F]">
                {match.home_score}
              </span>
              <span className="text-white/30">:</span>
              <span className="font-mono font-bold text-3xl tabular-nums text-[#00D37F]">
                {match.away_score}
              </span>
            </>
          ) : (
            <span className="font-display text-lg text-white/20">VS</span>
          )}
        </div>
        <div className="flex items-center gap-3 justify-end min-w-0">
          <span className="font-head text-base sm:text-lg text-right truncate">{match.away_team}</span>
          <span className="text-3xl drop-shadow">{match.away_flag}</span>
        </div>
      </div>

      {/* Bet slip area */}
      <div className="px-5 py-4 border-t border-white/5 bg-[#0a0a0a]">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.22em] font-bold text-white/40">
            <span className="w-4 h-[2px] bg-[#00D37F]" /> {t("your_slip")}
          </div>
          <div className="flex items-center gap-1 font-mono text-[10px]">
            <span className="px-1.5 py-0.5 bg-[#00D37F]/15 text-[#00D37F] border border-[#00D37F]/30 font-bold">
              {t("bet_pills_exact")}
            </span>
            <span className="px-1.5 py-0.5 bg-[#FFB703]/15 text-[#FFB703] border border-[#FFB703]/30 font-bold">
              {t("bet_pills_result")}
            </span>
          </div>
        </div>

        {user ? (
          <div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                max={30}
                value={home}
                onChange={(e) => setHome(e.target.value)}
                disabled={locked}
                data-testid={`predict-home-${match.id}`}
                className="w-14 h-12 px-2 text-center bg-black border border-white/20 focus:border-[#00D37F] focus:outline-none text-white font-mono text-2xl font-bold disabled:opacity-50"
                placeholder="-"
              />
              <span className="font-mono text-white/30 font-bold">:</span>
              <input
                type="number"
                min={0}
                max={30}
                value={away}
                onChange={(e) => setAway(e.target.value)}
                disabled={locked}
                data-testid={`predict-away-${match.id}`}
                className="w-14 h-12 px-2 text-center bg-black border border-white/20 focus:border-[#00D37F] focus:outline-none text-white font-mono text-2xl font-bold disabled:opacity-50"
                placeholder="-"
              />
              <button
                onClick={save}
                disabled={locked || saving}
                data-testid={`predict-save-${match.id}`}
                className="ml-auto h-12 px-5 bg-[#00D37F] hover:bg-[#00E78B] text-black font-head text-xs disabled:opacity-40 disabled:bg-white/10 disabled:text-white/60 transition-colors"
              >
                {saving ? "…" : locked ? t("save_btn_locked") : t("save")}
              </button>
            </div>
            {!locked && (
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
                  {t("closes_at")} {formatColombiaTime(match.kickoff)}{" "}
                  <span className="text-[#00D37F]/80">{t("colombia_time_short")}</span>
                </span>
                {remaining && (
                  <span
                    className={`inline-flex items-center gap-1.5 font-mono text-[11px] font-bold px-2 py-0.5 border tabular-nums ${
                      urgent
                        ? "text-[#FF2D45] border-[#FF2D45]/40 bg-[#FF2D45]/10 animate-pulse"
                        : "text-[#00D37F] border-[#00D37F]/40 bg-[#00D37F]/10"
                    }`}
                    data-testid={`countdown-${match.id}`}
                  >
                    <Clock className="w-3 h-3" />
                    {remaining}
                  </span>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="text-xs text-white/50 font-mono uppercase tracking-wider">
            — {t("login")} —
          </div>
        )}

        {prediction && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[11px] text-white/50">
              PICK:{" "}
              <span className="text-white font-bold">
                {prediction.home_score}–{prediction.away_score}
              </span>
            </span>
            {finished && (
              <span
                className={`font-mono font-bold text-[11px] px-2 py-0.5 border ${
                  prediction.points_awarded >= 5
                    ? "text-[#00D37F] bg-[#00D37F]/10 border-[#00D37F]/40"
                    : prediction.points_awarded > 0
                    ? "text-[#FFB703] bg-[#FFB703]/10 border-[#FFB703]/40"
                    : "text-white/40 bg-white/5 border-white/10"
                }`}
                data-testid={`pred-points-${match.id}`}
              >
                {prediction.points_awarded > 0 ? "+" : ""}
                {prediction.points_awarded} PTS{" "}
                {prediction.points_awarded >= 5
                  ? "· EXACT"
                  : prediction.points_awarded > 0
                  ? "· OUTCOME"
                  : "· MISS"}
              </span>
            )}
          </div>
        )}
      </div>

      {match.venue && (
        <div className="px-5 py-2 text-[10px] font-mono uppercase tracking-wider text-white/30 border-t border-white/5">
          📍 {match.venue}
        </div>
      )}
    </div>
  );
}

export default function Matches() {
  const { user, refreshUser } = useAuth();
  const { t } = useI18n();
  const [matches, setMatches] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [filter, setFilter] = useState("all");
  const [groupFilter, setGroupFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [resultAlert, setResultAlert] = useState(null);
  const seenFinishedRef = useRef(null); // Set of match ids already seen as finished

  const load = async () => {
    try {
      const [m, p] = await Promise.all([
        api.get("/matches"),
        user ? api.get("/predictions/me") : Promise.resolve({ data: [] }),
      ]);
      // Detect newly-finished matches (not on first load) to trigger alert
      if (seenFinishedRef.current !== null) {
        const prevSeen = seenFinishedRef.current;
        const newlyFinished = m.data.filter(
          (mt) => mt.status === "finished" && !prevSeen.has(mt.id)
        );
        if (newlyFinished.length > 0) {
          // Show alert for the most-recently-finished one (last in list by result_at if available)
          const target = newlyFinished
            .slice()
            .sort((a, b) =>
              (b.result_at || "").localeCompare(a.result_at || "")
            )[0];
          try {
            const lb = await api.get("/leaderboard");
            // Build awards: get all predictions for this match? Backend exposes only via result endpoint.
            // Fallback: synthesize awards from current user prediction list.
            const myPred = (p.data || []).find((pp) => pp.match_id === target.id);
            const awards = myPred
              ? [
                  {
                    user_id: user.id,
                    username: user.username,
                    name: user.name,
                    prediction: `${myPred.home_score}-${myPred.away_score}`,
                    awarded: myPred.points_awarded || 0,
                    reason: myPred.scored_reason || "none",
                  },
                ]
              : [];
            setResultAlert({ match: target, awards, leaderboard: lb.data });
            // Refresh user balance for the navbar/scoreboard chip
            refreshUser?.();
          } catch (_) {
            // ignore
          }
        }
      }
      seenFinishedRef.current = new Set(
        m.data.filter((mt) => mt.status === "finished").map((mt) => mt.id)
      );
      setMatches(m.data);
      setPredictions(p.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Poll every 45 seconds for newly finished matches
    const interval = setInterval(load, 45000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const predByMatch = useMemo(() => {
    const map = {};
    predictions.forEach((p) => (map[p.match_id] = p));
    return map;
  }, [predictions]);

  const groups = useMemo(() => {
    const s = new Set();
    matches.forEach((m) => m.stage && s.add(m.stage));
    return Array.from(s).sort();
  }, [matches]);

  const filtered = matches.filter((m) => {
    if (groupFilter !== "all" && m.stage !== groupFilter) return false;
    const k = new Date(m.kickoff).getTime();
    if (filter === "live") return m.status !== "finished" && k <= Date.now() && k + 2 * 60 * 60 * 1000 >= Date.now();
    if (filter === "upcoming") return m.status !== "finished" && k > Date.now();
    if (filter === "finished") return m.status === "finished";
    return true;
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="matches-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] font-mono font-bold text-[#00D37F]">
            {t("nav_matches")} · MUNDIAL 2026
          </div>
          <h1 className="font-display text-5xl sm:text-6xl mt-2">{t("tagline")}</h1>
          <p className="text-sm text-white/50 mt-2 font-mono">{t("points_rule")}</p>
        </div>
        {user && (
          <div className="scoreboard px-5 py-3 text-right">
            <div className="text-[10px] uppercase tracking-[0.3em] font-mono font-bold text-white/50">
              {t("balance")} · {t("your_points")}
            </div>
            <div
              className="font-mono text-5xl font-bold text-[#00D37F] tabular-nums"
              data-testid="user-points"
            >
              {user.points ?? 0}
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {[
          ["all", t("filter_all")],
          ["upcoming", t("filter_upcoming")],
          ["live", t("filter_live")],
          ["finished", t("filter_finished")],
        ].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            data-testid={`filter-${k}`}
            className={`h-10 px-5 text-[11px] uppercase tracking-[0.2em] font-head border transition-colors ${
              filter === k
                ? "bg-[#00D37F] text-black border-[#00D37F]"
                : "bg-transparent text-white/60 border-white/15 hover:text-[#00D37F] hover:border-[#00D37F]/50"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6 pb-4 border-b border-white/5">
        <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 mr-1">
          {t("filter_by_group")}:
        </span>
        <button
          onClick={() => setGroupFilter("all")}
          data-testid="group-filter-all"
          className={`h-8 px-3 text-[10px] uppercase tracking-[0.2em] font-bold border transition-colors ${
            groupFilter === "all"
              ? "bg-[#00D37F] text-black border-[#00D37F]"
              : "bg-transparent text-white/50 border-white/10 hover:text-[#00D37F] hover:border-[#00D37F]/40"
          }`}
        >
          {t("all_groups")}
        </button>
        {groups.map((g) => (
          <button
            key={g}
            onClick={() => setGroupFilter(g)}
            data-testid={`group-filter-${g.replace(/\s+/g, "-")}`}
            className={`h-8 px-3 text-[10px] uppercase tracking-[0.2em] font-bold border transition-colors ${
              groupFilter === g
                ? "bg-[#00D37F] text-black border-[#00D37F]"
                : "bg-transparent text-white/50 border-white/10 hover:text-[#00D37F] hover:border-[#00D37F]/40"
            }`}
          >
            {g}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-white/50">…</div>
      ) : filtered.length === 0 ? (
        <div className="text-white/50 text-sm">{t("no_matches")}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6" data-testid="matches-grid">
          {filtered.map((m) => (
            <MatchCard
              key={m.id}
              match={m}
              prediction={predByMatch[m.id]}
              onSaved={load}
            />
          ))}
        </div>
      )}
      {resultAlert && (
        <MatchResultAlert result={resultAlert} onClose={() => setResultAlert(null)} />
      )}
    </div>
  );
}
